import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Slide,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const RejectModal = ({
    open,
    handleClose,
    handleConfirm,
    request,
    isLoading = false,
}) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!reason.trim()) {
            setError('Please provide a reason for rejection');
            return;
        }
        setError('');
        handleConfirm(reason);
    };

    const handleCloseModal = () => {
        setReason('');
        setError('');
        handleClose();
    };

    if (!request) return null;

    const getValue = (value) => value || 'N/A';

    return (
        <Dialog
            open={open}
            onClose={handleCloseModal}
            TransitionComponent={Transition}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2.5,
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}>
                        Reject Request
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        size="small"
                        disabled={isLoading}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
                    {/* Warning Icon */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: '#fef3c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 3,
                        }}
                    >
                        <Icon
                            icon="solar:close-circle-bold"
                            width={40}
                            height={40}
                            style={{ color: '#f59e0b' }}
                        />
                    </Box>

                    {/* Message */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem',
                        }}
                    >
                        Are you sure you want to reject this request?
                    </Typography>

                    {/* Request Details */}
                    <Box
                        sx={{
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            px: 2,
                            py: 1.5,
                            mt: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                            {getValue(request?.societyName)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                        >
                            Society ID: {getValue(request?.societyId)}
                        </Typography>
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Credits:
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {request?.credits?.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Amount:
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    ₹{request?.amount?.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Rejection Reason Input */}
                    <Box sx={{ mt: 3, textAlign: 'left' }}>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                        >
                            Rejection Reason <span style={{ color: '#ef4444' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter reason for rejection..."
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                if (error) setError('');
                            }}
                            disabled={isLoading}
                            error={!!error}
                            helperText={error}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: '0.875rem',
                                }
                            }}
                        />
                    </Box>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1.5 }}>
                <button
                    type="button"
                    className="btn btn-outline-secondary radius-8 px-20 py-10 text-sm fw-medium"
                    onClick={handleCloseModal}
                    disabled={isLoading}
                    style={{ flex: 1 }}
                >
                    <Icon icon="solar:close-circle-outline" className="me-6" />
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-warning radius-8 px-20 py-10 text-sm fw-medium"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    style={{ flex: 1, backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: '#fff' }}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-8" role="status" aria-hidden="true"></span>
                            Rejecting...
                        </>
                    ) : (
                        <>
                            <Icon icon="solar:close-circle-bold" className="me-6" />
                            Yes, Reject
                        </>
                    )}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default RejectModal;
